import {
  suggestMaxUpdatesForWorkout,
  type MaxSuggestion,
} from "./maxSuggest.js";
import {
  suggestMaxRegressionsForWorkout,
  type CurrentMax,
} from "./maxRegression.js";
import type { Workout } from "./schemas/workout.js";

// What a just-saved workout proposes for each MAX, pure logic behind
// `packages/ui/src/Maxes/useMaxBumpOffer.ts`, which POSTs it to the server
// (#182). Both surfaces read this one function, so they cannot drift on what
// counts as a candidate. Lifted in 1.34.0 (ALIGNMENT_AUDIT item 3, now
// next/archive/ALIGNMENT_AUDIT.md).

// Bidirectional MAX (next/measurement-contract.md «Il MAX dinamico: il come»). A post-save MAX prompt now
// flows in BOTH directions: the classic upward bump (a new best) and the
// downward revision (a stale MAX a maximal-effort set proves too high).
export type MaxBumpDirection = "raise" | "lower";

export type MaxBumpCandidate = {
  exerciseId: string;
  // The proposed value + provenance. For a 'lower' candidate this carries
  // the regression's suggestedKg/e1RM/fromSet so every existing reader
  // (`c.suggestion.suggestedKg`) works unchanged regardless of direction.
  suggestion: MaxSuggestion;
  // 'raise' (default; omitted by legacy web-built candidates) or 'lower'.
  direction?: MaxBumpDirection;
  // 'lower' only: the stale stored MAX being revised down + its age, for
  // the regression banner copy ("MAX sceso: {currentMaxKg} → …,
  // {weeksSinceMax} sett."). Absent on raises.
  regression?: { currentMaxKg: number; weeksSinceMax: number };
};

export type PendingMaxBumps = {
  workoutId: string;
  candidates: MaxBumpCandidate[];
};

// Evaluate a just-saved workout against the user's current MAXes; return
// the candidate list, or null if no suggestion fires. Pure function: the
// one clock read is `now`, the regression gate's anchor (injectable).
export const computeMaxBumpCandidates = (params: {
  workout: Workout;
  exercisePatterns: Parameters<
    typeof suggestMaxUpdatesForWorkout
  >[0]["exercisePatterns"];
  currentMaxes: Record<string, number | null | undefined>;
  // Optional {valueKg, setAt} map enabling the DOWNWARD (regression)
  // direction (next/measurement-contract.md «Il MAX dinamico: il come»). When omitted (e.g. web, which doesn't
  // do lower yet) only upward bumps are produced — behaviour-preserving.
  // `nowMs` anchors the regression staleness gate; defaults to `now`.
  currentMaxesDetailed?: Record<string, CurrentMax | null | undefined>;
  // Per-exercise best qualifying e1RM over the recent corroboration
  // window (excluding the just-saved workout). Gates the DOWNWARD
  // direction so a single light day can't lower a MAX below a recent
  // stronger session (Antenore 2026-06-26). Absent ⇒ no corroboration.
  recentBestE1RM?: Record<string, number>;
  // Sibling of the line above, same window, same walk (`MX-11`): how many
  // separate sessions carried a qualifying e1RM. Enough of them, none
  // reaching the MAX ⇒ the MAX was never corroborated and its calendar
  // immunity is bypassed. ⛔ The RAISE path never reads it.
  recentSessionCount?: Record<string, number>;
  workoutId: string;
  now?: number;
}): PendingMaxBumps | null => {
  const now = params.now ?? Date.now();

  // RAISE — the classic upward bump (unchanged).
  const raises = suggestMaxUpdatesForWorkout({
    workout: params.workout,
    exercisePatterns: params.exercisePatterns,
    currentMaxes: params.currentMaxes,
  });
  const byExercise = new Map<string, MaxBumpCandidate>();
  for (const [exerciseId, suggestion] of Object.entries(raises)) {
    byExercise.set(exerciseId, { exerciseId, suggestion, direction: "raise" });
  }

  // LOWER — the downward revision. Only when a detailed map is supplied.
  // Raise wins if an exercise somehow qualifies for both (a new best is
  // unambiguous; the two gates are in practice mutually exclusive).
  if (params.currentMaxesDetailed) {
    const regressions = suggestMaxRegressionsForWorkout({
      workout: params.workout,
      currentMaxes: params.currentMaxesDetailed,
      recentBestE1RM: params.recentBestE1RM,
      recentSessionCount: params.recentSessionCount,
      nowMs: now,
    });
    for (const [exerciseId, reg] of Object.entries(regressions)) {
      if (byExercise.has(exerciseId)) continue;
      byExercise.set(exerciseId, {
        exerciseId,
        // Reuse the MaxSuggestion shape so every `.suggestion` reader works.
        suggestion: {
          suggestedKg: reg.suggestedKg,
          e1RM: reg.e1RM,
          fromSet: reg.fromSet,
        },
        direction: "lower",
        regression: {
          currentMaxKg: reg.currentMaxKg,
          weeksSinceMax: reg.weeksSinceMax,
        },
      });
    }
  }

  const candidates = [...byExercise.values()];
  if (candidates.length === 0) return null;
  return {
    workoutId: params.workoutId,
    candidates,
  };
};
